/* eslint-disable @typescript-eslint/no-empty-function */
import { clearCache, useRequest } from 'ahooks';
import React, { createContext, useContext, useState } from 'react';

import { useMemberInfo } from '@/hooks/useMemberInfo';
import { useFetchUserSecurityProfile } from '@/hooks/useUserSecurityProfile';
import { getAntiPhishingCode } from '@/services/antiPhishingCode';
import { isUnderReview as queryIsUnderReview } from '@/services/googleUnbind';

import { useUserStore } from '../useUser';

const ModalContext = createContext({
  antiPhishingCode: '',
  hasAntiPhishing: false,
  gettingAntiPhishingCode: false,
  getAntiPhishing: () => {},
  updateAntiPhishing: () => {},
  userSecurityProfile: {
    error: undefined as Error | undefined,
    loading: false,
    profile: undefined as any,
    userSecurityInfo: {} as any,
    mutate: (callback: any) => {},
    updateUserSecurityProfile: () => {},
    run: () => {},
  },
  getProfileV2: (onError?: (error: any) => void) => {},
  subMemberBind2faSkipFirstStep: false,
  gaIsUnderReview: false,
  checkGoogleUnderReview: () => {},
  setGaUnbinding: (unbingding: boolean) => {},
  gaUnbinding: false,
  avatar: { avatar_url: '', avatar_id: 0 },
  setAvatar: (avatar: any) => {},
});

export const SecuritySettingModalDataProvider = ({
  children,
}: {
  children: any;
}) => {
  const {
    userProfile: { userInfo: user },
    msgError,
  } = useUserStore();
  const [antiPhishingCode, setAntiPhishingCode] = useState<string>('');
  const [hasAntiPhishing, setHasAntiPhishing] = useState<boolean>(false);
  const userSecurityProfile = useFetchUserSecurityProfile();
  const { subMemberBind2faSkipFirstStep, avatar, setAvatar } = useMemberInfo();
  const [gaIsUnderReview, setGaIsUnderReview] = useState(false);
  const [gaUnbinding, setGaUnbinding] = useState(false);

  // 检查ga是否处于解绑审核中
  const checkGoogleUnderReview = () => {
    if (user.has_google2fa) {
      const params = {
        member_id: user.id,
        login_name: user.vague_email_v2
          ? user.vague_email_v2
          : user.vague_mobile,
        only_check_ga_int: 1,
      };
      queryIsUnderReview(params)
        .then((res: any) => {
          if (res && res.ret_code === 0) {
            if (res.result.audit_status === 1) {
              setGaIsUnderReview(true);
            }
          } else {
            msgError(res);
          }
        })
        .catch((error: any) => {
          msgError(error);
        });
    }
  };

  const { runAsync, loading: gettingAntiPhishingCode } = useRequest(
    getAntiPhishingCode,
    {
      debounceWait: 50,
      manual: true,
      cacheKey: 'antiPhishingCode',
      staleTime: 30 * 1000,
    },
  );

  // 请求用户防钓鱼码
  const getAntiPhishing = () => {
    runAsync()
      .then((res: any) => {
        if (res.ret_code === 0) {
          setAntiPhishingCode(res.result.antiPhishingCode || '');
          if (res.result.antiPhishingCode) {
            setHasAntiPhishing(true);
          } else {
            setHasAntiPhishing(false);
          }
        }
      })
      .catch((error) => {
        msgError(error);
      });
  };

  const updateAntiPhishing = () => {
    clearCache('antiPhishingCode');
    getAntiPhishing();
  };

  // 请求profile-v2接口
  const getProfileV2 = () => {
    userSecurityProfile.run();
  };

  return (
    <ModalContext.Provider
      value={{
        antiPhishingCode,
        hasAntiPhishing,
        gettingAntiPhishingCode,
        getAntiPhishing,
        updateAntiPhishing,
        userSecurityProfile,
        getProfileV2,
        subMemberBind2faSkipFirstStep,
        gaIsUnderReview,
        checkGoogleUnderReview,
        setGaUnbinding,
        gaUnbinding,
        avatar,
        setAvatar,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useSecuritySettingModalData = () => useContext(ModalContext);
